/* One file's card. Metadata, not paragraphs: a name, three facts, a trail, one result line. */

.card {
  display: grid;
  gap: 14px;
  padding: 16px 18px;
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  background: var(--panel);
  animation: card-in 280ms var(--ease-out) both;
}

@keyframes card-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.head {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
}

.fileIcon {
  color: var(--accent);
}

.titles {
  min-width: 0;
}

.name {
  margin: 0;
  overflow: hidden;
  font-size: var(--t-head);
  font-weight: 650;
  color: var(--ink);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta {
  margin: 2px 0 0;
  font-size: var(--t-min);
  color: var(--muted);
  font-variant-numeric: lining-nums tabular-nums;
}

.refusal {
  margin: 0;
  padding: 8px 12px;
  border-left: 3px solid var(--crit);
  background: var(--crit-w);
  color: var(--crit);
  font-size: var(--t-dense);
}

.actions {
  display: flex;
  gap: 8px;
}

/* The match: four facts, no sentences. **One column**: the facts take the card's full width and the
 * actions sit under them. Beside each other (the facts `1fr`, the actions `auto`) the two buttons
 * took ~220px of a 450px drawer and left the four facts ~40px each, so every word wrapped one letter
 * per line (live, release 05faf566). */
.match {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px 18px;
  align-items: start;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-ctl);
  background: var(--panel-2);
}

.facts {
  display: grid;
  /* As many tracks as fit at a readable width (two in a narrow drawer, four in a wide one). */
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 12px 18px;
  margin: 0;
}

.match dt {
  font-size: var(--t-min);
  color: var(--muted);
}

.match dd {
  margin: 2px 0 0;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.none {
  font-weight: 500;
  color: var(--muted);
}

.flag {
  font-weight: 600;
  color: var(--warn);
}

/* The review-queue line (ADR-0166): a note beside the coverage cell, in ink and not in the state's
 * colour, so it cannot be mistaken for the window having turned. */
.queued,
.queuedPending {
  margin: 0;
  font-size: var(--t-min);
  color: var(--ink-2);
}

.queuedPending {
  color: var(--muted);
}

.matchAction {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.cell {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  font-size: var(--t-min);
  color: var(--muted);
}

.cellLabel {
  font-variant-numeric: lining-nums tabular-nums;
}

/* The coverage cell turns when its state changes: one flip, transform only. */
.turn {
  display: inline-block;
  animation: cell-turn 420ms var(--ease-out) both;
  transform-origin: center;
}

@keyframes cell-turn {
  from {
    opacity: 0.3;
    transform: rotateX(80deg);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.result {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  align-items: baseline;
  margin: 0;
  padding: 10px 14px;
  border-radius: var(--radius-ctl);
  background: var(--ok-w);
  color: var(--ok);
  font-size: var(--t-dense);
}

.result strong {
  font-family: var(--font-display);
  font-size: var(--t-figure);
  font-weight: 400;
  color: var(--ink);
}

.primary,
.ghost,
.link {
  min-height: var(--target-min);
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: var(--radius-ctl);
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 650;
  cursor: pointer;
}

.primary {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  background: var(--accent);
  color: var(--on-fill);
}

.primary:hover:not(:disabled) {
  background: var(--accent-h);
}

.primary:disabled {
  border-color: var(--btn-off-bg);
  background: var(--btn-off-bg);
  color: var(--btn-off-ink);
  cursor: default;
}

.ghost {
  border-color: var(--line-2);
  background: var(--panel);
  color: var(--ink-2);
}

.ghost:hover {
  background: var(--panel-2);
}

.link {
  padding: 0;
  background: none;
  color: var(--accent);
  text-decoration: underline;
}

@media (max-width: 760px) {
  .match {
    grid-template-columns: minmax(0, 1fr);
  }

  .facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (prefers-reduced-motion: reduce) {
  .card,
  .turn {
    animation: none;
  }
}
